import {HalImage} from "@cm/hal"
import {AnyImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {Region} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"

// Streaming a whole source image into the atlas in one request can require more pages than the atlas
// budget provides for large textures. This constant bounds how much of a source region is requested at
// once; tune based on profiling. Mirrors the `maxPagesPerDim` precedent already used by
// BakedMaskCache's shard-upload loop.
export const DEFAULT_MAX_PAGES_PER_BATCH_DIM = 10 // max maxPagesPerBatchDim^2 pages resident per batch

export const tileRegionIntoBatches = (region: Region, pageSize: number, maxPagesPerBatchDim: number): Region[] => {
    const maxSizePerDim = maxPagesPerBatchDim * pageSize
    const numBatchesX = Math.max(1, Math.ceil(region.width / maxSizePerDim))
    const numBatchesY = Math.max(1, Math.ceil(region.height / maxSizePerDim))
    const batches: Region[] = []
    for (let by = 0; by < numBatchesY; by++) {
        for (let bx = 0; bx < numBatchesX; bx++) {
            const x = region.x + bx * maxSizePerDim
            const y = region.y + by * maxSizePerDim
            const width = Math.min(maxSizePerDim, region.x + region.width - x)
            const height = Math.min(maxSizePerDim, region.y + region.height - y)
            batches.push({x, y, width, height})
        }
    }
    return batches
}

// Fetches `region` from `sourceImage` in page-budget-limited shards, invoking `callback` once per shard
// with the resident batch image, releasing it before requesting the next one. Bounds how many atlas pages
// are resident at once regardless of how large `region` is. `callback` may do GPU-only work (as in
// TiledReductionHelper) or read data back to the CPU (as in BakedMaskCache's upload path) — this
// helper only owns the streaming/release loop, not what's done with each shard. Note: `batchHalImage` is a
// reference to the full (virtual) source image, not a view cropped to `batchRegion` — GPU work that assumes
// a (0,0)-relative source (e.g. ReductionHelperHal.reduce()) must crop it first via
// `batchHalImage.createView({type: "sub-image", region: batchRegion})`.
export const forEachBatchRegion = async (
    sourceImage: AnyImageOpHalData,
    region: Region,
    maxPagesPerBatchDim: number,
    callback: (batchHalImage: HalImage, batchRegion: Region) => void | Promise<void>,
    options?: UpdateImageOptions,
): Promise<Region[]> => {
    const batches = tileRegionIntoBatches(region, sourceImage.ctx.pageSize, maxPagesPerBatchDim)
    for (const batchRegion of batches) {
        const batchHalImageRef = await sourceImage.getUpdatedHalImage({...batchRegion, mipLevel: 0}, options)
        try {
            await callback(batchHalImageRef.halImage, batchRegion)
        } finally {
            batchHalImageRef.release()
        }
    }
    return batches
}
